/**
 * Abwesenheiten für das Panel „// heute abwesend".
 *
 * **Bewusst ohne Grund/Art der Abwesenheit.** Wer heute fehlt, ist eine
 * Organisationsinformation; *warum*, ist es nicht: Krankheit ist ein
 * Gesundheitsdatum (DSGVO Art. 9) und eine für alle sichtbare Anzeige wäre
 * zusätzlich mitbestimmungspflichtig. Das Feld existiert deshalb hier nicht –
 * auch nicht optional, damit es später niemand „nur mal kurz" einbaut.
 */

export interface Absence {
  id: string;
  /** Anzeigename, wie im Profil hinterlegt. */
  name: string;
  /** ISO-Datum (YYYY-MM-DD) des ersten Abwesenheitstags. */
  from: string;
  /** ISO-Datum des letzten Abwesenheitstags (inklusive). */
  to: string;
}

const dayMonth = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit' });

function parse(iso: string): Date | null {
  const date = new Date(`${iso}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Zeitraum als kurze Zeile: „14.08." bei einem Tag, sonst „14.08. – 18.08.".
 * Unlesbare Werte fallen auf einen Strich zurück, statt „Invalid Date" zu zeigen.
 */
export function formatAbsenceRange(from: string, to: string): string {
  const start = parse(from);
  const end = parse(to);
  if (!start && !end) return '–';
  if (!start) return dayMonth.format(end!);
  if (!end) return dayMonth.format(start);

  const a = dayMonth.format(start);
  const b = dayMonth.format(end);
  return a === b ? a : `${a} – ${b}`;
}

/** Ist die Abwesenheit am Stichtag aktiv? (Grenztage zählen mit.) */
export function isAbsentOn(absence: Absence, today: Date): boolean {
  const start = parse(absence.from);
  const end = parse(absence.to);
  if (!start || !end) return false;
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return start.getTime() <= day && day <= end.getTime();
}

/** Heute abwesende Personen, alphabetisch – die Liste wird vorgelesen wie eine Liste. */
export function absentToday(absences: Absence[], today: Date = new Date()): Absence[] {
  return absences
    .filter((absence) => isAbsentOn(absence, today))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

/**
 * Antwort der People-Function in die eigene Form bringen.
 *
 * Bewusst ein Filter und keine Typzusicherung: die Daten kommen aus einer
 * anderen App. Sollte dort je ein Feld dazukommen (Abwesenheitsart, Grund),
 * fällt es hier heraus, statt im Hub aufzutauchen.
 */
export function parseAbsences(payload: unknown): Absence[] {
  if (!Array.isArray(payload)) return [];
  const out: Absence[] = [];
  for (const raw of payload) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    const { id, name, from, to } = row;
    if (typeof id !== 'string' || typeof name !== 'string') continue;
    if (typeof from !== 'string' || typeof to !== 'string') continue;
    out.push({ id, name, from, to });
  }
  return out;
}
